/**
 * PatrioticScrollbar — fully decorative scrollbar that replaces the
 * native browser one on AHP-branded pages.
 *
 * Design v2 — looks like a vertical American flag:
 *
 *   ┃ ⋆ ┃          ← gradient fade-in from page (no hard top cap)
 *   ┃   ┃
 *   ┃   ┃          ← track: subtle 13-stripe red+cream pattern
 *   ┃   ┃            with hand-stitched cream centerline
 *   ┃ ┌─┐ ┃
 *   ┃ │NN│ ┃        ← THUMB navy canton (top ~38%): 4 white stars
 *   ┃ │★★│ ┃          in 2-row grid
 *   ┃ │★★│ ┃
 *   ┃ ├──┤ ┃
 *   ┃ │R │ ┃        ← THUMB stripe band (bottom ~62%):
 *   ┃ │ W│ ┃          alternating red+cream, 7 reds, 6 creams
 *   ┃ │R │ ┃          per US flag convention
 *   ┃ │ W│ ┃
 *   ┃ │R │ ┃
 *   ┃ └──┘ ┃
 *   ┃   ┃
 *   ┃ ⋆ ┃          ← gradient fade-out to page (no hard bottom cap)
 *
 * Thumb is interactive: drag to scroll, hover shows gold glow + lift.
 * Track is clickable for jump-scroll. Component opts out on routes
 * that own their scrolling (/hospital-rankings, /map).
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useBrand } from '@/config/brands';
import { isNativeApp } from '@/lib/platform';

const ROUTES_WITHOUT_PATRIOTIC_SCROLLBAR = [
  '/hospital-rankings',
  '/map',
];

const PALETTE = {
  red: '#B22234',
  redDeep: '#9A1E2D',
  cream: '#FAF9F6',
  parchment: '#FBF7EE',
  navy: '#001238',
  navyMid: '#002868',
  gold: '#C5A55A',
  goldDeep: '#9A7B33',
  goldLight: '#E6CF8F',
};

const TRACK_WIDTH = 18;
const FADE_HEIGHT = 60;          // gradient fade at top + bottom of track
const THUMB_MIN_HEIGHT = 80;     // ensures stripes + canton are readable
// Sit beneath the app chrome (header banner ~500-600, footer nav ~10000) so the
// scrollbar never paints over the fixed bars. Was max-int (on top of everything).
const Z_INDEX = 300;

function FlagStarsCanton({ height, width }: { height: number; width: number }) {
  // Render a 2x2 grid of white stars in the navy canton. Star size scales
  // with available width. If width is too narrow, fall back to 1 star.
  const cols = width >= 14 ? 2 : 1;
  const rows = height >= 18 ? 2 : 1;
  const starSize = Math.max(3, Math.min(6, Math.floor(width / 4)));
  const stars: React.ReactNode[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      stars.push(
        <svg
          key={`${r}-${c}`}
          width={starSize}
          height={starSize}
          viewBox="0 0 24 24"
          style={{
            position: 'absolute',
            top: `${(r + 0.5) * (100 / rows)}%`,
            left: `${(c + 0.5) * (100 / cols)}%`,
            transform: 'translate(-50%, -50%)',
            filter: 'drop-shadow(0 0.5px 0 rgba(0,0,0,0.3))',
          }}
          aria-hidden
        >
          <path
            d="M12 2 L14.59 8.41 L21.5 9.27 L16.5 13.97 L17.82 20.81 L12 17.27 L6.18 20.81 L7.5 13.97 L2.5 9.27 L9.41 8.41 Z"
            fill="#FFFFFF"
          />
        </svg>
      );
    }
  }
  return <>{stars}</>;
}

function FlagStripes({ height, width }: { height: number; width: number }) {
  // 13 alternating stripes, top stripe red. Compute the per-stripe height
  // and render them as positioned divs (purer than CSS repeating-gradient
  // which can leave half-pixel seams at narrow widths).
  const stripeCount = 13;
  const stripeH = height / stripeCount;
  const stripes: React.ReactNode[] = [];
  for (let i = 0; i < stripeCount; i++) {
    stripes.push(
      <div
        key={i}
        style={{
          position: 'absolute',
          top: i * stripeH,
          left: 0,
          right: 0,
          height: stripeH + 0.5, // 0.5 overlap to hide subpixel seams
          background: i % 2 === 0 ? PALETTE.red : PALETTE.cream,
        }}
        aria-hidden
      />
    );
  }
  return <>{stripes}</>;
}

export function PatrioticScrollbar() {
  const brand = useBrand();
  const location = useLocation();
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [thumbHeight, setThumbHeight] = useState(THUMB_MIN_HEIGHT);
  const [thumbTop, setThumbTop] = useState(0);
  const [trackHeight, setTrackHeight] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const dragStartRef = useRef<{ y: number; scroll: number } | null>(null);

  const skipRoute = ROUTES_WITHOUT_PATRIOTIC_SCROLLBAR.some(p =>
    location.pathname.startsWith(p));
  // /home-beta sets `data-explorer-expanded="true"` on body when the
  // rankings-demo iframe goes into in-page-takeover mode; the scrollbar
  // gutter sits over the iframe with nothing meaningful to scroll, so
  // hide it while the takeover is active. Re-render is forced by the
  // window resize event the takeover already fires.
  const [explorerExpanded, setExplorerExpanded] = useState(false);
  useEffect(() => {
    const check = () => setExplorerExpanded(document.body.getAttribute('data-explorer-expanded') === 'true');
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { attributes: true, attributeFilter: ['data-explorer-expanded'] });
    return () => mo.disconnect();
  }, []);
  // Suppress the faux scrollbar entirely in the native app — a custom scrollbar
  // reads as "web page", not "app". Native uses the OS scroll indicator.
  const active = brand.key === 'ahp' && !skipRoute && !explorerExpanded && !isNativeApp();

  useEffect(() => {
    if (!active) return;
    document.body.setAttribute('data-patriotic-scrollbar', 'on');
    return () => { document.body.removeAttribute('data-patriotic-scrollbar'); };
  }, [active]);

  const recompute = useCallback(() => {
    const docH = document.documentElement.scrollHeight;
    const viewH = window.innerHeight;
    const scrollY = window.scrollY;
    const overflow = docH - viewH;
    if (overflow <= 0) { setIsVisible(false); return; }
    setIsVisible(true);
    const track = trackRef.current;
    if (!track) return;
    const trackH = track.clientHeight;
    setTrackHeight(trackH);
    const proportional = (viewH / docH) * trackH;
    const thumbH = Math.max(THUMB_MIN_HEIGHT, proportional);
    setThumbHeight(thumbH);
    const travel = trackH - thumbH;
    const t = (scrollY / overflow) * travel;
    setThumbTop(Math.max(0, Math.min(travel, t)));
  }, []);

  useEffect(() => {
    if (!active) return;
    // Recompute on mount + on every route change (location.pathname dep).
    // Schedule a few staggered recomputes so async-loaded content that
    // expands scrollHeight after first paint still gets measured.
    recompute();
    const t1 = setTimeout(recompute, 50);
    const t2 = setTimeout(recompute, 200);
    const t3 = setTimeout(recompute, 600);
    let raf: number | null = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { recompute(); raf = null; });
    };
    const onResize = () => recompute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    // Watch the document for size changes — catches lazy-loaded images,
    // async-loaded scenario data, modal opens/closes, etc.
    let mutRaf: number | null = null;
    const scheduleRecompute = () => {
      if (mutRaf) return;
      mutRaf = requestAnimationFrame(() => { recompute(); mutRaf = null; });
    };
    const ro = new ResizeObserver(scheduleRecompute);
    ro.observe(document.documentElement);
    if (document.body) ro.observe(document.body);
    // Watch for DOM mutations (added/removed nodes) — covers cases where
    // total height changes without the immediate child being resized.
    // rAF-throttled to avoid spammy recomputes on chatty pages.
    const mo = new MutationObserver(scheduleRecompute);
    mo.observe(document.body, { childList: true, subtree: true });
    const interval = setInterval(recompute, 1000);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
      mo.disconnect();
      clearInterval(interval);
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      if (raf) cancelAnimationFrame(raf);
      if (mutRaf) cancelAnimationFrame(mutRaf);
    };
  }, [active, recompute, location.pathname]);

  const onThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStartRef.current = { y: e.clientY, scroll: window.scrollY };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;
      const dy = e.clientY - dragStartRef.current.y;
      const docH = document.documentElement.scrollHeight;
      const viewH = window.innerHeight;
      const overflow = docH - viewH;
      const travel = trackHeight - thumbHeight;
      if (travel <= 0) return;
      const scrollDelta = (dy / travel) * overflow;
      window.scrollTo({
        top: Math.max(0, Math.min(overflow, dragStartRef.current.scroll + scrollDelta)),
        behavior: 'auto',
      });
    };
    const onUp = () => { setIsDragging(false); dragStartRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, trackHeight, thumbHeight]);

  const onTrackClick = (e: React.MouseEvent) => {
    if (e.target === thumbRef.current || thumbRef.current?.contains(e.target as Node)) return;
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const yWithinTrack = e.clientY - rect.top;
    const docH = document.documentElement.scrollHeight;
    const viewH = window.innerHeight;
    const overflow = docH - viewH;
    const target = ((yWithinTrack - thumbHeight / 2) / (rect.height - thumbHeight)) * overflow;
    window.scrollTo({ top: Math.max(0, Math.min(overflow, target)), behavior: 'smooth' });
  };

  if (!active) return null;
  if (!isVisible) return null;

  // Flag canton occupies the top 38% of the thumb (matches US flag spec
  // where the canton is 7/13 of the field height — slightly less in our
  // case so the stripes get more vertical room).
  const cantonHeight = thumbHeight * 0.385;
  const stripesHeight = thumbHeight - cantonHeight;
  const innerThumbWidth = TRACK_WIDTH - 4; // border + small margin

  // Hover/drag visual state
  const thumbBorder = isHovered || isDragging ? PALETTE.gold : `${PALETTE.navy}66`;
  const thumbShadow = isDragging
    ? `inset 0 0 6px rgba(0,0,0,0.45), 0 0 0 1px ${PALETTE.gold}`
    : isHovered
    ? `0 0 14px ${PALETTE.gold}aa, 0 4px 10px rgba(0,18,56,0.4)`
    : `0 2px 6px rgba(0,18,56,0.30)`;

  return (
    <div
      role="scrollbar"
      aria-orientation="vertical"
      aria-valuenow={Math.round((window.scrollY / Math.max(1, document.documentElement.scrollHeight - window.innerHeight)) * 100)}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: TRACK_WIDTH,
        zIndex: Z_INDEX,
        pointerEvents: 'auto',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        // Track: subtle 13-stripe pattern in parchment+red, masked at the
        // top and bottom so the bar fades into whatever page color is
        // behind it (header gradients, footer red bands, etc.) — no more
        // hard color caps that clash with the page chrome.
        background: `
          repeating-linear-gradient(
            180deg,
            ${PALETTE.red}1f 0px,
            ${PALETTE.red}1f 7px,
            ${PALETTE.parchment}00 7px,
            ${PALETTE.parchment}00 14px
          ),
          linear-gradient(180deg, ${PALETTE.parchment} 0%, ${PALETTE.cream} 100%)
        `,
        WebkitMaskImage: `linear-gradient(180deg, transparent 0, #000 ${FADE_HEIGHT}px, #000 calc(100% - ${FADE_HEIGHT}px), transparent 100%)`,
        maskImage: `linear-gradient(180deg, transparent 0, #000 ${FADE_HEIGHT}px, #000 calc(100% - ${FADE_HEIGHT}px), transparent 100%)`,
        borderLeft: `0.5px solid ${PALETTE.gold}33`,
      }}
      onClick={onTrackClick}
    >
      {/* Track-spanning clickable surface — the actual ref used for math. */}
      <div
        ref={trackRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ position: 'absolute', inset: 0, cursor: 'pointer' }}
      >
        {/* The Thumb = a vertical American flag */}
        <div
          ref={thumbRef}
          onMouseDown={onThumbMouseDown}
          style={{
            position: 'absolute',
            top: thumbTop,
            left: 2,
            right: 2,
            height: thumbHeight,
            borderRadius: 2,
            border: `1px solid ${thumbBorder}`,
            boxShadow: thumbShadow,
            cursor: isDragging ? 'grabbing' : 'grab',
            overflow: 'hidden',
            transition: isDragging
              ? 'none'
              : 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
            transform: isHovered && !isDragging ? 'scaleX(1.12)' : 'scaleX(1)',
            transformOrigin: 'center',
          }}
        >
          {/* Stripes layer — full height, gets covered by canton at top */}
          <div style={{ position: 'absolute', inset: 0 }}>
            <FlagStripes height={thumbHeight - 2} width={innerThumbWidth} />
          </div>
          {/* Navy canton overlay on top portion */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: cantonHeight,
              background: `linear-gradient(135deg, ${PALETTE.navyMid} 0%, ${PALETTE.navy} 100%)`,
              borderBottom: `0.5px solid ${PALETTE.gold}55`,
            }}
          >
            <FlagStarsCanton height={cantonHeight} width={innerThumbWidth} />
          </div>
        </div>
      </div>
    </div>
  );
}
